import React, { useEffect, useRef } from 'react';
import { Link } from 'react-scroll';
import { SideBarNavItemType } from '@/common';
import styles from '@/styles/SideBarNav.module.scss';

const SCROLL_DURATION = 500;
const MOBILE_BREAKPOINT = 768;

export const SideBarNavItem = ({
  url = '',
  title,
  parent,
  isActive,
  width,
  navigateToDifferentPart,
}: SideBarNavItemType & {
  width: number;
  isActive: string;
  navigateToDifferentPart: (isNewActive: string) => void;
}) => {
  const isGroup = ['lp', 'gp', 'related stories'].includes(title.toLowerCase());
  const isGroupVisible = isGroup && width > MOBILE_BREAKPOINT;
  const isLinkVisible = !isGroup && width > MOBILE_BREAKPOINT;
  // thematic-section divider before every top-level item except the first (Overview)
  const sectionClass = !parent && title.toLowerCase() !== 'overview' ? styles['sidebar-section-divider'] : '';

  const fallbackTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(fallbackTimer.current), []);

  // react-scroll marks a section active only while scrollY - offset >= Math.floor(sectionTop),
  // and a click lands scrollY exactly on that bound; a sub-pixel undershoot misses it and
  // fires onSetInactive (unwired) instead, so claim the highlight once the animation ends
  const claimActiveAfterScroll = () => {
    clearTimeout(fallbackTimer.current);
    fallbackTimer.current = setTimeout(() => navigateToDifferentPart(title), SCROLL_DURATION);
  };

  if (isGroup) {
    if (isGroupVisible) {
      return (
        <li
          className={`${styles['sidebar-item']} ${styles['sidebar-item-parent-cursor']} ${sectionClass}`}
        >
          <span className={styles['sidebar-item-parent']}>{title}</span>
        </li>
      );
    } else return <></>;
  }

  if (isLinkVisible) {
    return (
      <li className={`${styles['sidebar-item']} ${sectionClass}`}>
        <Link
          to={url}
          spy={true}
          smooth={true}
          offset={-40}
          duration={SCROLL_DURATION}
          onSetActive={() => {
            navigateToDifferentPart(title);
          }}
        >
          <span
            className={`${styles['sidebar-item-link']} ${
              isActive === title && styles['sidebar-item-link--active']
            }`}
            onClick={claimActiveAfterScroll}
          >
            {title}
          </span>
        </Link>
      </li>
    );
  }

  return (
    <Link
      to={url}
      spy={true}
      smooth={true}
      offset={width <= MOBILE_BREAKPOINT ? -70 : 0}
      duration={SCROLL_DURATION}
      onSetActive={() => {
        navigateToDifferentPart(title);
      }}
    >
      <span
        className={`${styles['sidebar-item-link']} ${
          isActive === title && styles['sidebar-item-link--active']
        }`}
        onClick={claimActiveAfterScroll}
      >
        {title} {parent && `(${parent})`}
      </span>
    </Link>
  );
};
